import {
  BaseNav,
  type BaseHeaderMobileBreakpoint,
  ChakraBreakpoints,
  FeatureFlagButton,
  RouteAuthenticationRequirement,
  SupportButton,
  useAuth,
  useNavItemStore
} from '@cks-internal/react-base';
import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useBreakpointValue } from '@chakra-ui/react';
import { Box, type BoxProps, HStack, Image } from '../ui/chakraPrimitives';
import { ApplicationRoutes } from '../../constants/appRoutes.ts';
import { AppRoles } from '../../constants/appRoles.ts';
import logo from '../../assets/logo-full.png';
import mobileLogo from '../../assets/compass-logo-small.jpg';
import { CompassUser } from '../../globalTypes/user/compassUser.ts';
import { AutomationStatusBadge } from '../../features/emailAutomation/components/AutomationStatusBadge.tsx';
import { GoToJobSearch } from './GoToJobSearch.tsx';

type NarrowLayoutProps = {
  containerProps?: BoxProps;
};

const mobileHeaderBreakpoint: BaseHeaderMobileBreakpoint = 'xl';

export const Layout = (props: NarrowLayoutProps) => {
  const routeNav = useNavItemStore((state) => {
    return { items: state.getDefault(), get: state.get };
  });

  const profileItems: BaseNav.NavItem = useMemo(() => {
    const subItems: BaseNav.NavItem[] = [
      routeNav.get(ApplicationRoutes.account.myProfile),
      routeNav.get(ApplicationRoutes.account.logout)
    ];
    return {
      text: 'Profile',
      subItems: subItems,
      authenticationRequirement: RouteAuthenticationRequirement.Authenticated,
      allowedRoles: []
    };
  }, []);

  const auth = useAuth<CompassUser>();
  const showSupportAndBetaButtons = auth.user?.Roles?.includes(AppRoles.BetaUser) ?? false;
  const mobileLogoImage = (
    <Image width={'42px'} height={'42px'} objectFit='contain' src={mobileLogo} alt='Compass logo' />
  );
  const desktopLogoImage = <Image width={'160px'} height={['41px']} src={logo} alt='Compass logo' />;
  const logoImage =
    useBreakpointValue({
      base: mobileLogoImage,
      [mobileHeaderBreakpoint]: desktopLogoImage
    }) ?? mobileLogoImage;
  const headerLogo = <HStack>{logoImage}</HStack>;

  return (
    <Box as='section' width='100vw' height='100vh' overflowY='auto' bg={'#fafafa'}>
      <BaseNav.Header
        forceTwoLineMode={true}
        mobileBreakpoint={mobileHeaderBreakpoint}
        menuMode={'hover'}
        avatarName={`${auth.user?.FirstName} ${auth.user?.LastName}`}
        navItems={routeNav.items}
        profileItems={profileItems}
        environmentIndicator={{
          enabled: true
        }}
        logo={headerLogo}>
        {auth.isAuthenticated && (
          <>
            <GoToJobSearch />
            <AutomationStatusBadge />
            {showSupportAndBetaButtons && (
              <>
                <SupportButton useDisplayMedia={true} />
                <FeatureFlagButton />
              </>
            )}
          </>
        )}
      </BaseNav.Header>
      <ChakraBreakpoints />
      <Box
        w={{ base: '100%', full: '90%' }}
        margin={'auto'}
        paddingLeft={'5px'}
        paddingRight={'5px'}
        paddingTop={'10px'}
        {...props.containerProps}>
        <Outlet />
      </Box>
    </Box>
  );
};
